import { Plus, Store } from 'lucide-react'

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'

import { Company, Permission, Role } from '@/types'

import { useTranslation } from 'next-i18next'
import useAuth from '@/contexts/useAuth'
import Link from 'next/link'
import { Button } from '../ui/button'
import { usePathname } from 'next/navigation'
import { keyBy } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { UserService } from '@/lib/services/user'
import { useToast } from '@/hooks/use-toast'
import { sidebarItems } from '@/types/sidebar'

const getItems = (permissions: Permission[], company?: Company) => {
  const permissionsByKey = keyBy(permissions)
  const isStripeIndependent = company?.hasIndependentStripe ?? false

  let authorizedGroups: Record<string, any> = {}
  if (permissionsByKey[Permission.BookNowInternal]) {
    authorizedGroups.booking = []
  }

  // DEV-370: Group Marketplace is only meaningful for chain locations
  const belongsToChain = Boolean(company?.chainId)

  Object.keys(sidebarItems).forEach((group) => {
    const authorizedItems = sidebarItems[group].filter((item: any) => {
      if (isStripeIndependent && item.title === 'external_experiences') {
        return false
      }

      if (item.title === 'customers') {
        // Show Clienti if the user can see the CRM list, or (DEV-777) can
        // recover access codes on a codice-univoco chain. Keeps low roles off
        // the section on chains without access codes.
        const canViewList =
          !!permissionsByKey[Permission.CustomersViewAny] &&
          !!company?.enableMarketingConsent
        const canRecoverCodes =
          !!permissionsByKey[Permission.CustomersViewAccessCode] &&
          !!company?.chain?.usesAccessCodes
        return canViewList || canRecoverCodes
      }

      if (item.title === 'group_experiences' && !belongsToChain) {
        return false
      }

      return !item.permission || permissionsByKey[item.permission]
    })

    if (isStripeIndependent && group === 'external_experiences') {
      return false
    }

    if (authorizedItems.length > 0) {
      authorizedGroups[group] = authorizedItems
    }
  })

  return authorizedGroups
}

export function AppSidebar() {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const pathname = usePathname()
  const { toast } = useToast()

  const items = getItems(user?.permissions ?? [], user?.company)

  /**
   * Find the base list URL for the current pathname.
   * If we're on a detail page (e.g., /online-stores/abc123), return the list page URL (e.g., /online-stores).
   * Returns null if we're already on a list page.
   */
  const getListPageUrl = (): string | null => {
    // All known base URLs that have detail pages (with dynamic routes)
    // This covers both sidebar items and non-sidebar pages
    const baseUrls = [
      '/online-stores',
      '/experiences',
      '/bookings',
      '/master-orders',
      '/products',
      '/users',
      '/categories',
      '/tags',
      '/internal-labels',
      '/companies/organizer',
      '/companies/seller',
      '/companies/chain',
      '/chains',
      '/roles',
      '/sales-demos',
      '/inventories',
    ]

    // Find a base URL that the current pathname starts with
    for (const baseUrl of baseUrls) {
      if (pathname.startsWith(baseUrl) && pathname !== baseUrl) {
        // We're on a detail page (pathname is longer than the base URL)
        return baseUrl
      }
    }

    return null
  }

  const switchCompany = async (companyId: number) => {
    if (!user) {
      return
    }
    try {
      await UserService.switchAdminCompany(user, companyId)
      toast({ title: t('common:save_success'), variant: 'success' })

      // If we're on a detail page, redirect to the list page
      // This prevents staying on a resource that belongs to a different company
      const listPageUrl = getListPageUrl()
      if (listPageUrl) {
        window.location.href = listPageUrl
      } else {
        // DEV-898: drop the query, its filter ids belong to the previous company
        window.location.href = window.location.pathname
      }
    } catch (error: any) {
      console.error(error)
      toast({ description: error.message, variant: 'destructive' })
    }
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>
            <div className="pt-8">
              <Link className="logo flex justify-center" href="/">
                <span className="mr-2 text-4xl font-extrabold text-primary group-data-[collapsible=icon]:hidden">
                  ALOE
                </span>
              </Link>
            </div>
          </SidebarGroupLabel>
        </SidebarGroup>

        {user?.role === Role.Admin && (
          <Select
            value={user.companyId?.toString()}
            onValueChange={(value) => switchCompany(Number(value))}
          >
            <SelectTrigger className="border-none shadow-none focus:ring-0 focus:ring-ring hover:bg-accent px-4">
              <SelectValue placeholder="" />
            </SelectTrigger>
            <SelectContent>
              {user.manageableCompanies?.map(({ label, value }) => (
                <SelectItem value={value.toString()} key={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {Object.keys(items).map((group) =>
          group === 'booking' ? (
            <div
              className="px-4 mt-8 group-data-[collapsible=icon]:px-2"
              key="booking"
            >
              <Link href="/create-order">
                <Button className="w-full justify-between group-data-[collapsible=icon]:justify-center">
                  <span className="uppercase group-data-[collapsible=icon]:hidden">
                    {t('book_now')}
                  </span>

                  <Plus size={24} />
                </Button>
              </Link>
            </div>
          ) : (
            <SidebarGroup key={group}>
              <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
                <div className="mb-2 text-lg font-semibold tracking-tight text-black capitalize">
                  {t(group)}
                </div>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items[group].map((item: any) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={
                          item.url === '/'
                            ? pathname === '/'
                            : pathname.length > 1 &&
                              pathname.startsWith(item.url.split('?')[0])
                        }
                      >
                        {user?.role === Role.Organizer &&
                        item.title === 'internal_experiences' ? (
                          <Link href={item.url}>
                            <Store />
                            <span>{t('online_stores')}</span>
                          </Link>
                        ) : (
                          <Link href={item.url}>
                            <item.icon />
                            <span>{t(item.title)}</span>
                          </Link>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem></SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
